import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';

/** Disposer returned by {@link KeyboardShortcutService.register}. Call to remove the binding. */
export type ShortcutDisposer = () => void;

/** Modifier requirement for a shortcut. */
export interface ShortcutOptions {
  /** When true, the shortcut ALSO fires while an editable input/textarea/contenteditable is focused. @default false */
  allowInInput?: boolean;
  /** `preventDefault()` on match. @default true */
  preventDefault?: boolean;
  /** Human description used for the shortcut help modal. */
  description?: string;
}

interface Registration {
  id: number;
  key: string;
  handler: (event: KeyboardEvent) => void;
  options: Required<Pick<ShortcutOptions, 'allowInInput' | 'preventDefault'>> & Pick<ShortcutOptions, 'description'>;
}

const INPUT_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

let nextId = 1;

/**
 * Page-scoped keyboard shortcut registration. Returns a disposer; call it from
 * `ngOnDestroy` (or keep a handle in your component). Respects input focus by
 * default: shortcuts don't fire while the user is typing, unless the caller opts
 * in with `allowInInput`.
 *
 * Keys are specified in a normalized `modifier+key` form:
 * - `'cmd+k'`, `'ctrl+k'`, `'shift+?'`, `'meta+enter'`, `'escape'`, `'/'`
 *
 * `cmd` and `ctrl` are interchangeable cross-platform: `'cmd+k'` fires on both
 * ⌘K (macOS) and Ctrl+K (Windows/Linux).
 *
 * @example
 * private readonly shortcuts = inject(KeyboardShortcutService);
 * private disposers: ShortcutDisposer[] = [];
 *
 * ngOnInit() {
 *   this.disposers.push(
 *     this.shortcuts.register('cmd+k', () => this.openPalette(), { description: 'Open command palette' }),
 *     this.shortcuts.register('escape', () => this.close()),
 *   );
 * }
 *
 * ngOnDestroy() {
 *   this.disposers.forEach(d => d());
 * }
 *
 * @feature shared
 */
@Injectable({ providedIn: 'root' })
export class KeyboardShortcutService implements OnDestroy {
  private readonly zone = inject(NgZone);

  private readonly registrations = new Map<number, Registration>();
  private listenerAttached = false;
  private readonly boundHandler = this.onKeydown.bind(this);

  /** Register a shortcut. Returns a disposer. */
  register(key: string, handler: (event: KeyboardEvent) => void, options: ShortcutOptions = {}): ShortcutDisposer {
    const id = nextId++;
    const normalized = this.normalize(key);
    this.registrations.set(id, {
      id,
      key: normalized,
      handler,
      options: {
        allowInInput: options.allowInInput ?? false,
        preventDefault: options.preventDefault ?? true,
        description: options.description,
      },
    });
    this.ensureListener();
    return () => {
      this.registrations.delete(id);
      if (this.registrations.size === 0) this.detachListener();
    };
  }

  /** List all currently registered shortcuts (for a help modal). */
  list(): Array<{ key: string; description?: string }> {
    return Array.from(this.registrations.values()).map(r => ({
      key: r.key,
      description: r.options.description,
    }));
  }

  ngOnDestroy(): void {
    this.detachListener();
    this.registrations.clear();
  }

  private ensureListener(): void {
    if (this.listenerAttached) return;
    this.zone.runOutsideAngular(() => {
      document.addEventListener('keydown', this.boundHandler);
    });
    this.listenerAttached = true;
  }

  private detachListener(): void {
    if (!this.listenerAttached) return;
    document.removeEventListener('keydown', this.boundHandler);
    this.listenerAttached = false;
  }

  private onKeydown(event: KeyboardEvent): void {
    const key = this.eventKey(event);
    if (!key) return;
    const target = event.target as HTMLElement | null;
    const isInputContext = !!target && (INPUT_TAGS.has(target.tagName) || (target as HTMLElement).isContentEditable);

    for (const reg of this.registrations.values()) {
      if (reg.key !== key) continue;
      if (isInputContext && !reg.options.allowInInput) continue;
      if (reg.options.preventDefault) event.preventDefault();
      this.zone.run(() => reg.handler(event));
      return;
    }
  }

  private normalize(input: string): string {
    const parts = input
      .toLowerCase()
      .split('+')
      .map(p => p.trim())
      .filter(Boolean);
    const mods = new Set<string>();
    let key = '';
    for (const p of parts) {
      if (p === 'cmd' || p === 'ctrl' || p === 'meta' || p === 'control') {
        mods.add('mod');
      } else if (p === 'shift') {
        mods.add('shift');
      } else if (p === 'alt' || p === 'option') {
        mods.add('alt');
      } else {
        key = p;
      }
    }
    return [...[...mods].sort(), key].join('+');
  }

  private eventKey(event: KeyboardEvent): string | null {
    const key = event.key.toLowerCase();
    if (!key) return null;
    const mods = new Set<string>();
    if (event.metaKey || event.ctrlKey) mods.add('mod');
    if (event.shiftKey) mods.add('shift');
    if (event.altKey) mods.add('alt');
    return [...[...mods].sort(), key].join('+');
  }
}
